// @flow
import identity from 'lodash/fp/identity';

// types
import type { DropShipValue } from './drop-ship';
import type { Index } from './search-engine';

import type { ROE, ROA } from 'modules/type';
import type { ShippingMethods } from 'modules/settings';
import type { StreetAddress } from 'modules/street-address';

export type Location = ROE<{
  _id: string,
  address_lines: ROA<string>,
  client_fields: {
    drop_ship?: DropShipValue,
    ship_via_options?: ShippingMethods,
    ...
  },
  group_codes: ROA<string> | null,
  name: string,
  number: string,
  address?: StreetAddress,
  tags?: {
    Division?: ROA<string>,
  },
}>;

export type LocationOption = ROE<{
  name: string,
  value: string,
  label: string,
  labelLines: ROA<string>,
  divisions: ROA<string>,
  addressLines: ROA<string>,
}>;

export type SearchableLocation = ROE<{
  id: string,
  name: string,
  number: string,
  addressLines: ROA<string>,
  original: Location,
}>;

export const INDICES: $ReadOnlyArray<Index> = [
  { type: 'Field', property: ['name'] },
  { type: 'Field', property: ['number'] },
  { type: 'Field', property: ['addressLines'] },
];

export const asSearchable = (location: Location): SearchableLocation => ({
  // eslint-disable-next-line no-underscore-dangle
  id: location._id,
  name: location.name,
  number: location.number,
  addressLines: location.address_lines,
  original: location,
});

export const divisions = (location: Location): ROA<string> =>
  (location.tags || { ...null }).Division || [];

export const locationOption = (
  location: Location,
  includeLocationNumberInSelect?: boolean,
): LocationOption => {
  const labelLines = [
    location.name,
    ...(includeLocationNumberInSelect ? [location.number] : []),
    ...location.address_lines,
  ].filter(identity);

  return {
    value: location.number,
    name: location.name,
    label: labelLines.join(' '),
    labelLines,
    addressLines: location.address_lines,
    divisions: divisions(location),
  };
};

export const byNumber = (
  locations: Location[],
  number: string,
): Location | null => locations.find((l) => l.number === number) || null;

export const optionByNumber = (
  locationOptions: LocationOption[],
  number: string,
): LocationOption | null =>
  locationOptions.find((option) => option.value === number) || null;
